import * as React from "react"

import { cn } from "@/lib/utils"

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * The size variant of the input
   * @default "default"
   */
  size?: 'sm' | 'default' | 'lg';
  /**
   * The type of the input
   * @default "text"
   */
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  /**
   * Additional CSS classes to apply
   */
  className?: string;
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * The current value of the input
   */
  value?: string | number | readonly string[] | undefined;
  /**
   * The default value of the input
   */
  defaultValue?: string | number | readonly string[] | undefined;
  /**
   * Callback when the input value changes
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * Ref object for the input element
   */
  ref?: React.Ref<HTMLInputElement>;
}

// Pre-computed size classes for performance
const SIZE_CLASSES = {
  sm: "h-8 px-2 text-sm",
  default: "h-10 px-3 text-sm sm:h-11 sm:text-base",
  lg: "h-12 px-4 text-base sm:h-14 sm:text-lg",
} as const;

// Base input classes extracted for better caching
const BASE_INPUT_CLASSES = "flex rounded-md border border-input bg-background ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Optimized Input component with React.memo for performance
 * Prevents unnecessary re-renders when props haven't changed
 */
const Input = React.memo(React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", size = "default", value, ...props }, ref) => {
    // Convert null/undefined values to empty string to prevent React warnings
    const safeValue = React.useMemo(() => 
      value === null || value === undefined ? '' : value, 
      [value]
    );
    
    const memoizedClassName = React.useMemo(() => 
      cn(
        BASE_INPUT_CLASSES,
        SIZE_CLASSES[size],
        className
      ),
      [size, className]
    );
    
    return (
      <input
        type={type}
        className={memoizedClassName}
        ref={ref}
        value={safeValue}
        {...props}
      />
    )
  }
));

Input.displayName = "Input"

export { Input }
